import { PenLine, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Says where an account's numbers come from: the bank, or the user.
 *
 * Both kinds sit in the same list, and nothing told them apart — a balance the
 * bank refreshes on its own looked exactly like one typed last month. Only the
 * synchronised ones are marked in lists: the badge is the exception, so the
 * plain rows read as "mine, kept by hand" without a badge on every line.
 *
 * `showManual` turns the manual side on for screens showing one account at a
 * time, where naming both costs nothing and answers the question outright.
 */
export function AccountSourceBadge({
  source,
  showManual = false,
  compact = false,
  className,
}: {
  source: 'manual' | 'powens';
  showManual?: boolean;
  /** Icon only, for places with no room for a word. Still named for screen readers. */
  compact?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const isSynced = source === 'powens';

  if (!isSynced && !showManual) return null;

  const label = isSynced ? t('accounts.synced') : t('accounts.manualSource');
  const hint = isSynced ? t('accounts.syncedHint') : t('accounts.manualSourceHint');
  const Icon = isSynced ? RefreshCw : PenLine;

  if (compact) {
    return (
      <span
        title={hint}
        aria-label={label}
        role="img"
        className={cn(
          'inline-flex shrink-0 items-center',
          isSynced ? 'text-brand-700 dark:text-brand-400' : 'text-muted-foreground',
          className
        )}
      >
        <Icon className="h-3 w-3" aria-hidden="true" />
      </span>
    );
  }

  return (
    <span
      title={hint}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-px text-[10px] font-semibold',
        isSynced
          ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-400'
          : 'bg-muted text-muted-foreground',
        className
      )}
    >
      <Icon className="h-2.5 w-2.5" aria-hidden="true" />
      {label}
    </span>
  );
}
